import { useEffect, useMemo, useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Checkbox,
  Collapse,
  DatePicker,
  Descriptions,
  Divider,
  Drawer,
  Empty,
  Form,
  Input,
  Modal,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Timeline,
  Tooltip,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  ExportOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import type { Dayjs } from 'dayjs'
import { supabase } from '@/lib/supabase'
import { formatCST, nowCST, pickerToUTCISO } from '@/lib/time'
import { useDebounced, useIsMobile } from '@/lib/hooks'
import { IpAddressLookup } from '@/components/IpAddressLookup'
import { AdminFilterBar } from '@/components/AdminFilterBar'
import { AdminMobileList } from '@/components/AdminMobileList'
import { ReplyTemplatePicker } from '@/components/ReplyTemplatePicker'
import { FeedbackTriageTag } from '@/components/FeedbackTriageTag'
import {
  TRANSFER_AREA_LABELS,
  TRANSFER_ISSUE_TYPE_LABELS,
  TRANSFER_OUTCOME_MESSAGES,
  TRANSFER_REPLY_MAX,
  TRANSFER_TITLE_MAX,
  TRIAGE_FILTER_OPTIONS,
  TRIAGE_LABELS,
  TRIAGE_LABEL_COLORS,
  triageFilterParam,
  validateTransferForm,
  type TransferArea,
  type TransferIssueType,
  type TransferResult,
  type TriageLabel,
} from '@/lib/feedback-triage'
import { transferEpisodeFeedbackToProductIssue } from '@/lib/feedback-triage-rpc'
import {
  ISSUE_CATEGORY_LABELS,
  ISSUE_LIST_STATUS_LABELS,
  ISSUE_SORT_LABELS,
  ISSUE_STATUS_LABELS,
  OCCURRENCE_STATUS_LABELS,
  PLATFORM_LABELS,
  REASON_LABELS,
  TIME_FIELD_LABELS,
  classifyListFailure,
  currentPageSelectionLabel,
  expectedVersionsMap,
  formatEpisodeLabel,
  formatIssueScope,
  hydrateIssueTargets,
  isReviewOverdue,
  isRpcUnavailable,
  isUuid,
  issueSortColumnState,
  issueSortFromColumn,
  issueStatusColor,
  listFailureMessage,
  newIdempotencyKey,
  occurrenceStatusColor,
  parseIssueDetail,
  parseIssueList,
  parseIssueStats,
  parseOccurrenceList,
  reasonColor,
  reasonLabel,
  rpcErrorMessage,
  sanitizeDiagnostics,
  suggestedReasonText,
  splitIssueReplies,
  sumIssueImpact,
  type FeedbackOccurrenceStatus,
  type FeedbackReason,
  type IssueCategory,
  type IssueListStatus,
  type IssueReply,
  type IssueRow,
  type IssueSortKey,
  type IssueStatus,
  type OccurrenceRow,
  type TimeField,
} from '@/lib/episode-feedback-triage'

export const Route = createFileRoute('/_authed/episode-feedback')({
  component: EpisodeFeedbackPage,
})

type PageView = 'issues' | 'raw'
type ResolveAction = 'resolve' | 'dismiss'
type ClientPlatformFilter = 'web' | 'android'

interface RawFeedbackRow {
  id: number
  episode_id: number
  anime_id: number
  anime_title: string | null
  episode_number: string | number | null
  episode_kind: string | null
  episode_title: string | null
  user_id: string | null
  user_username: string | null
  user_display_name: string | null
  reason: FeedbackReason
  description: string | null
  status: FeedbackOccurrenceStatus
  resolved_by: string | null
  resolver_username: string | null
  resolved_at: string | null
  resolver_note: string | null
  user_agent: string | null
  ip_address: string | null
  player_code: string | null
  is_hls: boolean | null
  created_at: string
  updated_at: string
}

interface RawListResult {
  ok: boolean
  total?: number
  rows?: Array<RawFeedbackRow>
  error?: string
}

const PAGE_SIZE = 50
const NEXTJS_BASE_URL =
  import.meta.env.VITE_NEXTJS_BASE_URL || 'https://dm.xifanacg.com'

const SETTABLE_ISSUE_STATUSES: Array<IssueStatus> = [
  'observing',
  'investigating',
  'resolved',
  'no_anomaly',
]

async function adminRpc(name: string, args: Record<string, unknown> = {}) {
  const { data, error } = await (supabase as any)
    .schema('admin')
    .rpc(name, args)
  if (error) throw error
  return data
}

function hlsModeText(isHls: boolean | null): string {
  if (isHls === true) return 'HLS'
  if (isHls === false) return '普通源'
  return '未知格式'
}

function hlsModeColor(isHls: boolean | null): string | undefined {
  if (isHls === true) return 'blue'
  if (isHls === false) return 'default'
  return undefined
}

function PlaybackContextTags({
  playerCode,
  isHls,
}: {
  playerCode: string | null
  isHls: boolean | null
}) {
  const hasPlayerCode = !!playerCode
  const hasMode = isHls !== null
  if (!hasPlayerCode && !hasMode) {
    return <Typography.Text type="secondary">—</Typography.Text>
  }

  return (
    <Space size={[4, 4]} wrap>
      {hasPlayerCode ? (
        <Tag color="cyan" style={{ marginInlineEnd: 0 }}>
          {playerCode}
        </Tag>
      ) : (
        <Tag style={{ marginInlineEnd: 0 }}>未知通道</Tag>
      )}
      <Tag color={hlsModeColor(isHls)} style={{ marginInlineEnd: 0 }}>
        {hlsModeText(isHls)}
      </Tag>
    </Space>
  )
}

function buildPlaybackInspectUrl(row: {
  anime_id: number
  episode_id: number
  player_code: string | null
}): string {
  const base = NEXTJS_BASE_URL.replace(/\/+$/, '')
  const url = new URL(`/anime/${row.anime_id}/play/${row.episode_id}`, base)
  if (row.player_code) url.searchParams.set('source', row.player_code)
  return url.toString()
}

function invalidateIssueQueries(
  qc: ReturnType<typeof useQueryClient>,
  issueId?: string | null,
) {
  qc.invalidateQueries({ queryKey: ['episode_feedback_issues'] })
  qc.invalidateQueries({ queryKey: ['episode_feedback_issue_stats'] })
  qc.invalidateQueries({ queryKey: ['episode_feedback'] })
  if (issueId) {
    qc.invalidateQueries({ queryKey: ['episode_feedback_issue', issueId] })
    qc.invalidateQueries({
      queryKey: ['episode_feedback_issue_occurrences', issueId],
    })
  }
}

function EpisodeFeedbackPage() {
  const isMobile = useIsMobile()
  const [view, setView] = useState<PageView>('issues')
  const statsQuery = useQuery({
    queryKey: ['episode_feedback_issue_stats'],
    retry: false,
    queryFn: async () =>
      parseIssueStats(await adminRpc('get_episode_feedback_issue_stats')),
  })
  const issueUnavailable =
    statsQuery.isError && isRpcUnavailable(statsQuery.error)

  useEffect(() => {
    if (issueUnavailable) setView('raw')
  }, [issueUnavailable])

  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <Card
        title="剧集反馈"
        extra={
          <Segmented<PageView>
            value={view}
            onChange={setView}
            options={[
              {
                label: '问题事件',
                value: 'issues',
                disabled: issueUnavailable,
              },
              { label: '原始反馈', value: 'raw' },
            ]}
          />
        }
      >
        {issueUnavailable ? (
          <Alert
            type="warning"
            showIcon
            message="问题事件接口未启用"
            description="后端事件化 RPC 尚未对当前环境开放。已保留原始反馈流程；启用后不会用空列表假装没有问题。"
            style={{ marginBottom: 12 }}
          />
        ) : (
          <Alert
            type="info"
            showIcon
            message="打开事件，看证据，回复并更新。"
            description={
              // 手机首屏留给列表：说明文字只在桌面展开
              isMobile
                ? undefined
                : '四态：观察中 / 排查中 / 已解决 / 未发现异常。公开回复会通知该事件涉及的用户。合并拆分不发回复。打开播放页只辅助检查。'
            }
            style={{ marginBottom: 12 }}
          />
        )}

        {view === 'issues' && !issueUnavailable ? (
          <IssueQueue statsQuery={statsQuery} />
        ) : (
          <RawFeedbackView />
        )}
      </Card>
    </Space>
  )
}

function IssueQueue({
  statsQuery,
}: {
  statsQuery: ReturnType<typeof useQuery<ReturnType<typeof parseIssueStats>>>
}) {
  const qc = useQueryClient()
  const { message } = AntdApp.useApp()
  const isMobile = useIsMobile()

  const [tab, setTab] = useState<IssueListStatus>('open')
  const [page, setPage] = useState(1)
  const [category, setCategory] = useState<IssueCategory | undefined>()
  const [triageLabel, setTriageLabel] = useState<TriageLabel | undefined>()
  const [playerCode, setPlayerCode] = useState('')
  const [platform, setPlatform] = useState<ClientPlatformFilter | undefined>()
  const [timeField, setTimeField] = useState<TimeField>('last_occurred')
  const [sortKey, setSortKey] = useState<IssueSortKey>('last_occurred_desc')
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null)
  const [incidentId, setIncidentId] = useState('')
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounced(search.trim(), 300)
  const debouncedPlayerCode = useDebounced(playerCode.trim(), 300)
  const debouncedIncidentId = useDebounced(incidentId.trim(), 300)

  const [selectedKeys, setSelectedKeys] = useState<string[]>([])
  const [detailId, setDetailId] = useState<string | null>(null)
  const [mergeOpen, setMergeOpen] = useState(false)

  useEffect(() => {
    setPage(1)
    setSelectedKeys([])
  }, [
    tab,
    category,
    triageLabel,
    platform,
    timeField,
    sortKey,
    debouncedSearch,
    debouncedPlayerCode,
    debouncedIncidentId,
    range?.[0]?.valueOf(),
    range?.[1]?.valueOf(),
  ])

  useEffect(() => {
    setSelectedKeys([])
  }, [page])

  const incidentFilter = debouncedIncidentId
    ? isUuid(debouncedIncidentId)
      ? debouncedIncidentId
      : null
    : null
  const incidentFilterInvalid =
    !!debouncedIncidentId && !isUuid(debouncedIncidentId)

  const listQuery = useQuery({
    queryKey: [
      'episode_feedback_issues',
      {
        tab,
        page,
        category,
        triageLabel,
        playerCode: debouncedPlayerCode,
        platform,
        timeField,
        sortKey,
        from: pickerToUTCISO(range?.[0]),
        to: pickerToUTCISO(range?.[1]),
        incidentId: incidentFilter,
        search: debouncedSearch,
      },
    ],
    enabled: !incidentFilterInvalid,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const listed = parseIssueList(
        await adminRpc('list_episode_feedback_issues', {
          p_status: tab,
          p_category: category ?? null,
          p_player_code: debouncedPlayerCode || null,
          p_platform: platform ?? null,
          p_incident_id: incidentFilter,
          p_search: debouncedSearch || null,
          p_time_field: timeField,
          p_from: pickerToUTCISO(range?.[0]),
          p_to: pickerToUTCISO(range?.[1]),
          p_limit: PAGE_SIZE,
          p_offset: (page - 1) * PAGE_SIZE,
          p_sort: sortKey,
          p_triage_label: triageFilterParam(triageLabel),
        }),
      )
      listed.rows = await hydrateIssueTargets(listed.rows, async (issueId) => {
        const occurrences = parseOccurrenceList(
          await adminRpc('list_episode_feedback_issue_occurrences', {
            p_issue_id: issueId,
            p_limit: 5,
            p_offset: 0,
          }),
        )
        return occurrences.rows
      })
      return listed
    },
  })

  const rows = listQuery.data?.rows ?? []
  const total = listQuery.data?.total ?? 0
  const stats = statsQuery.data
  const listFailure = listQuery.isError
    ? classifyListFailure(listQuery.error)
    : null
  const selectedRows = rows.filter((row) => selectedKeys.includes(row.id))

  const activeSort = issueSortColumnState(sortKey)
  const usersSortOrder = activeSort.field === 'users' ? activeSort.order : null
  const timeSortOrder =
    activeSort.field === 'last_occurred' ? activeSort.order : null

  const columns: ColumnsType<IssueRow> = useMemo(
    () => [
      {
        title: '番剧 / 剧集',
        render: (_: unknown, row) => {
          const scope = formatIssueScope(row)
          return (
            <Space direction="vertical" size={0}>
              <Typography.Text style={{ fontSize: 13 }}>
                {scope.title}
              </Typography.Text>
              {scope.subtitle ? (
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  {scope.subtitle}
                </Typography.Text>
              ) : null}
            </Space>
          )
        },
      },
      {
        title: '状态',
        width: 120,
        render: (_: unknown, row) => (
          <Space direction="vertical" size={0}>
            <Tag color={issueStatusColor(row.status)}>
              {ISSUE_STATUS_LABELS[row.status]}
            </Tag>
            {row.assignment_locked ? (
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                已锁定归属
              </Typography.Text>
            ) : null}
          </Space>
        ),
      },
      {
        title: '类别',
        width: 130,
        render: (_: unknown, row) => (
          <Tag color={reasonColor(row.category)}>
            {reasonLabel(row.category)}
          </Tag>
        ),
      },
      {
        title: (
          <Tooltip title="事件下当前报告的 AI 标签（人工改过的以人工为准，去重）。只有带描述的报告会打标；在详情的原始报告里修改">
            标签
          </Tooltip>
        ),
        width: 150,
        render: (_: unknown, row) => (
          <IssueTriageLabels labels={row.triage_labels} />
        ),
      },
      {
        title: (
          <Tooltip title="独立反馈人数。点表头按影响面排序（服务端排序，跨全量筛选结果，不是当前页）">
            影响
          </Tooltip>
        ),
        key: 'users',
        width: 150,
        // sorter: true = 服务端排序；antd 不会在本地重排，也不会用当前页冒充全量顺序
        sorter: true,
        sortDirections: ['descend', 'ascend'],
        sortOrder: usersSortOrder,
        showSorterTooltip: false,
        render: (_: unknown, row) => (
          <Typography.Text
            style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}
          >
            {row.distinct_user_count} 人 · {row.distinct_episode_count} 集 ·{' '}
            {row.occurrence_count} 报
          </Typography.Text>
        ),
      },
      {
        title: '线路 / 平台',
        width: 180,
        render: (_: unknown, row) => (
          <Space size={[4, 4]} wrap>
            {row.player_codes.length > 0 ? (
              row.player_codes.map((code) => (
                <Tag key={code} color="cyan" style={{ marginInlineEnd: 0 }}>
                  {code}
                </Tag>
              ))
            ) : (
              <Tag style={{ marginInlineEnd: 0 }}>未知通道</Tag>
            )}
            {row.platforms.map((platformCode) => (
              <Tag key={platformCode} style={{ marginInlineEnd: 0 }}>
                {PLATFORM_LABELS[platformCode as ClientPlatformFilter] ??
                  platformCode}
              </Tag>
            ))}
          </Space>
        ),
      },
      {
        title: 'Incident',
        width: 160,
        render: (_: unknown, row) =>
          row.incident ? (
            <Space direction="vertical" size={0}>
              <Typography.Text style={{ fontSize: 12 }}>
                {row.incident.title}
              </Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                {row.incident.state === 'resolved' ? '已恢复' : '进行中'} ·
                关联不是已解决
              </Typography.Text>
            </Space>
          ) : (
            <Typography.Text type="secondary">未关联</Typography.Text>
          ),
      },
      {
        title: (
          <Tooltip
            title={`列表按${TIME_FIELD_LABELS[timeField]}筛选（CST）；此列排序按末次发生时间，服务端排序`}
          >
            首末发生
          </Tooltip>
        ),
        key: 'last_occurred',
        width: 150,
        sorter: true,
        sortDirections: ['descend', 'ascend'],
        sortOrder: timeSortOrder,
        showSorterTooltip: false,
        render: (_: unknown, row) => (
          <Space direction="vertical" size={0}>
            <Typography.Text
              style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}
            >
              {formatCST(row.first_occurred_at, 'MM-DD HH:mm')}
            </Typography.Text>
            <Typography.Text
              type="secondary"
              style={{ fontSize: 11, fontVariantNumeric: 'tabular-nums' }}
            >
              → {formatCST(row.last_occurred_at, 'MM-DD HH:mm')} CST
            </Typography.Text>
          </Space>
        ),
      },
      {
        title: '复查',
        width: 120,
        render: (_: unknown, row) =>
          row.review_due_at ? (
            <Tag color={isReviewOverdue(row) ? 'error' : 'default'}>
              {isReviewOverdue(row) ? '已超期 ' : ''}
              {formatCST(row.review_due_at, 'MM-DD HH:mm')}
            </Tag>
          ) : (
            <Typography.Text type="secondary">—</Typography.Text>
          ),
      },
      {
        title: '操作',
        width: 90,
        fixed: 'right',
        render: (_: unknown, row) => (
          <Button type="link" size="small" onClick={() => setDetailId(row.id)}>
            查看
          </Button>
        ),
      },
    ],
    [timeField, usersSortOrder, timeSortOrder],
  )

  return (
    <>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        {stats && stats.review_due > 0 ? (
          <Alert
            type="warning"
            showIcon
            message={`有 ${stats.review_due} 个观察中问题已到复查期限`}
            description="超期观察不会自动关闭。打开详情看证据，回复并更新为排查中。"
          />
        ) : null}

        {statsQuery.isError && !isRpcUnavailable(statsQuery.error) ? (
          <Alert
            type="error"
            showIcon
            message="问题事件计数加载失败"
            description={listFailureMessage(
              classifyListFailure(statsQuery.error),
            )}
          />
        ) : null}

        <div className="comments-tabs">
          <Segmented<IssueListStatus>
            value={tab}
            onChange={setTab}
            options={(
              Object.entries(ISSUE_LIST_STATUS_LABELS) as Array<
                [IssueListStatus, string]
              >
            ).map(([value, label]) => ({
              value,
              label:
                stats && value !== 'all'
                  ? `${label} ${stats[value] ?? 0}`
                  : label,
            }))}
          />
        </div>

        <AdminFilterBar
          activeCount={
            (category ? 1 : 0) +
            (triageLabel ? 1 : 0) +
            (playerCode.trim() ? 1 : 0) +
            (platform ? 1 : 0) +
            (timeField !== 'last_occurred' ? 1 : 0) +
            (sortKey !== 'last_occurred_desc' ? 1 : 0) +
            (range ? 1 : 0) +
            (isMobile && incidentId.trim() ? 1 : 0)
          }
          primary={
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="搜番剧 / 用户名"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 220 }}
            />
          }
          extra={
            <Tooltip title={isMobile ? '刷新' : undefined}>
              <Button
                icon={<ReloadOutlined />}
                aria-label="刷新"
                onClick={() => {
                  void listQuery.refetch()
                  void statsQuery.refetch()
                }}
                loading={listQuery.isFetching && !listQuery.isLoading}
              >
                {isMobile ? null : '刷新'}
              </Button>
            </Tooltip>
          }
        >
          <Select
            allowClear
            placeholder="类别"
            style={{ width: 180 }}
            value={category}
            onClear={() => setCategory(undefined)}
            onChange={setCategory}
            options={(
              Object.entries(ISSUE_CATEGORY_LABELS) as Array<
                [IssueCategory, string]
              >
            ).map(([value, label]) => ({ value, label }))}
          />
          <Select
            allowClear
            placeholder="标签"
            aria-label="按标签筛选"
            style={{ width: 140 }}
            value={triageLabel}
            onClear={() => setTriageLabel(undefined)}
            onChange={setTriageLabel}
            options={TRIAGE_FILTER_OPTIONS}
          />
          <Input
            allowClear
            placeholder="线路 player_code"
            value={playerCode}
            onChange={(e) => setPlayerCode(e.target.value)}
            style={{ width: 180 }}
          />
          <Select
            allowClear
            placeholder="平台"
            style={{ width: 140 }}
            value={platform}
            onClear={() => setPlatform(undefined)}
            onChange={setPlatform}
            options={[
              { value: 'web', label: PLATFORM_LABELS.web },
              { value: 'android', label: PLATFORM_LABELS.android },
            ]}
          />
          <Select<TimeField>
            value={timeField}
            onChange={setTimeField}
            style={{ width: 140 }}
            options={(
              Object.entries(TIME_FIELD_LABELS) as Array<[TimeField, string]>
            ).map(([value, label]) => ({ value, label }))}
          />
          <Select<IssueSortKey>
            value={sortKey}
            onChange={setSortKey}
            style={{ width: 150 }}
            aria-label="排序"
            options={(
              Object.entries(ISSUE_SORT_LABELS) as Array<[IssueSortKey, string]>
            ).map(([value, label]) => ({ value, label }))}
          />
          <DatePicker.RangePicker
            showTime={{ format: 'HH:mm' }}
            format="YYYY-MM-DD HH:mm"
            value={range}
            onChange={(next) =>
              setRange(next && next[0] && next[1] ? [next[0], next[1]] : null)
            }
            presets={[
              {
                label: '今天',
                value: [nowCST().startOf('day'), nowCST().endOf('day')],
              },
              {
                label: '近 7 天',
                value: [nowCST().subtract(7, 'day').startOf('day'), nowCST()],
              },
              {
                label: '近 30 天',
                value: [nowCST().subtract(30, 'day').startOf('day'), nowCST()],
              },
            ]}
          />
          {/* 手机上 incident 精确筛选并入筛选面板，不再单独占一个折叠区 */}
          {isMobile ? (
            <Input
              allowClear
              placeholder="incident UUID 精确筛选"
              value={incidentId}
              status={incidentFilterInvalid ? 'error' : undefined}
              onChange={(e) => setIncidentId(e.target.value)}
            />
          ) : null}
        </AdminFilterBar>

        {isMobile && incidentFilterInvalid ? (
          <Alert
            type="warning"
            showIcon
            message="incident 筛选需要完整 UUID。目前没有按名称检索的接口。"
          />
        ) : null}

        {/* 口径说明较长，手机上省略（筛选面板里的选项本身已说明含义） */}
        {isMobile ? null : (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            时间筛选使用{TIME_FIELD_LABELS[timeField]}，展示为
            CST（Asia/Shanghai）。
            排序与独立用户数、总数都由服务端计算，不是当前页合计：按「反馈人数」排的是
            整个筛选范围内的顺序，不是当前页里的顺序。按标签筛选时，事件下任一当前报告带该标签即命中。
          </Typography.Text>
        )}

        {isMobile ? null : (
          <Collapse
            ghost
            items={[
              {
                key: 'more-filters',
                label: '更多筛选',
                children: (
                  <Space direction="vertical" size={8}>
                    <Input
                      allowClear
                      placeholder="按已登记 incident UUID 精确筛选"
                      value={incidentId}
                      status={incidentFilterInvalid ? 'error' : undefined}
                      onChange={(e) => setIncidentId(e.target.value)}
                      style={{ width: 320 }}
                    />
                    {incidentFilterInvalid ? (
                      <Alert
                        type="warning"
                        showIcon
                        message="incident 筛选需要完整 UUID。目前没有按名称检索的接口。"
                      />
                    ) : (
                      <Typography.Text
                        type="secondary"
                        style={{ fontSize: 12 }}
                      >
                        列表按番剧/用户名搜索。没有服务端 incident 名称检索。
                      </Typography.Text>
                    )}
                  </Space>
                ),
              },
            ]}
          />
        )}

        {listFailure ? (
          <Alert
            type="error"
            showIcon
            message={listFailureMessage(listFailure)}
            description={
              listQuery.error instanceof Error
                ? listQuery.error.message
                : undefined
            }
            action={
              <Button size="small" onClick={() => void listQuery.refetch()}>
                重试
              </Button>
            }
          />
        ) : null}

        {selectedKeys.length > 0 && !isMobile ? (
          <Alert
            type="info"
            showIcon
            message={currentPageSelectionLabel(selectedKeys.length)}
            description="跨页或全筛选范围操作尚未开放。合并本身不通知用户。"
            action={
              <Space>
                <Button size="small" onClick={() => setSelectedKeys([])}>
                  清除选择
                </Button>
                <Button
                  size="small"
                  type="primary"
                  disabled={selectedKeys.length < 2}
                  onClick={() => setMergeOpen(true)}
                >
                  合并所选
                </Button>
              </Space>
            }
          />
        ) : null}

        <div className="admin-table-wrap">
          <Table<IssueRow>
            rowKey="id"
            size="middle"
            loading={listQuery.isLoading}
            columns={columns}
            dataSource={listFailure ? [] : rows}
            scroll={{ x: 1430 }}
            rowSelection={{
              selectedRowKeys: selectedKeys,
              preserveSelectedRowKeys: false,
              onChange: (keys) => setSelectedKeys(keys as string[]),
            }}
            locale={{
              emptyText: listQuery.isLoading ? (
                '加载中…'
              ) : listFailure ? (
                <Empty description={listFailureMessage(listFailure)} />
              ) : (
                <Empty description="没有匹配的问题事件" />
              ),
            }}
            pagination={{
              current: page,
              pageSize: PAGE_SIZE,
              total: listFailure ? 0 : total,
              showSizeChanger: false,
              showTotal: (totalRows) => `共 ${totalRows} 个问题`,
            }}
            onChange={(pagination, _filters, sorter, extra) => {
              if (extra.action === 'sort') {
                const active = Array.isArray(sorter) ? sorter[0] : sorter
                // 取消排序（order 为空）回到默认排序；排序变化由 sortKey 上的
                // effect 重置页码，避免用旧页码去取新排序下的数据。
                setSortKey(
                  issueSortFromColumn(active?.columnKey, active?.order) ??
                    'last_occurred_desc',
                )
                return
              }
              if (
                extra.action === 'paginate' &&
                pagination.current &&
                pagination.current !== page
              ) {
                setPage(pagination.current)
              }
            }}
            onRow={(row) => ({
              onClick: () => setDetailId(row.id),
            })}
          />
        </div>

        <AdminMobileList
          rows={listFailure ? [] : rows}
          rowKey={(row) => row.id}
          loading={listQuery.isLoading || listQuery.isFetching}
          page={page}
          pageSize={PAGE_SIZE}
          total={listFailure ? 0 : total}
          onPageChange={setPage}
          emptyContent={
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                listQuery.isLoading
                  ? '加载中…'
                  : listFailure
                    ? listFailureMessage(listFailure)
                    : '没有匹配的问题事件'
              }
            />
          }
          renderItem={(row) => {
            const scope = formatIssueScope(row)
            const checked = selectedKeys.includes(row.id)
            return (
              <article
                className="admin-mobile-record"
                role="button"
                tabIndex={0}
                aria-label={`查看问题：${scope.title}`}
                onClick={() => setDetailId(row.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setDetailId(row.id)
                  }
                }}
              >
                <div className="admin-mobile-record__lead">
                  {/* 勾选用于合并；点卡片其余区域打开详情 */}
                  <span
                    className="admin-mobile-record__select"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      checked={checked}
                      aria-label={`选择 ${scope.title}`}
                      onChange={(e) =>
                        setSelectedKeys((prev) =>
                          e.target.checked
                            ? [...prev, row.id]
                            : prev.filter((key) => key !== row.id),
                        )
                      }
                    />
                  </span>
                  <div className="episode-feedback-card__main">
                    <div className="admin-mobile-record__head">
                      <div className="admin-mobile-record__title">
                        <Typography.Text>{scope.title}</Typography.Text>
                        <div className="admin-mobile-record__meta">
                          <span>
                            {scope.subtitle || reasonLabel(row.category)}
                          </span>
                        </div>
                      </div>
                      <Tag color={issueStatusColor(row.status)}>
                        {ISSUE_STATUS_LABELS[row.status]}
                      </Tag>
                    </div>
                    <div className="admin-mobile-record__stats">
                      <span>
                        <strong>{row.distinct_user_count}</strong> 人
                      </span>
                      <span>
                        <strong>{row.distinct_episode_count}</strong> 集
                      </span>
                      <span>
                        <strong>{row.occurrence_count}</strong> 报
                      </span>
                      <span>
                        末次 {formatCST(row.last_occurred_at, 'MM-DD HH:mm')}
                      </span>
                    </div>
                    <div className="admin-mobile-record__badges">
                      <Tag color={reasonColor(row.category)}>
                        {reasonLabel(row.category)}
                      </Tag>
                      {row.player_codes.slice(0, 2).map((code) => (
                        <Tag key={code} color="cyan">
                          {code}
                        </Tag>
                      ))}
                      {row.player_codes.length > 2 ? (
                        <Tag>+{row.player_codes.length - 2}</Tag>
                      ) : null}
                      {row.platforms.map((code) => (
                        <Tag key={code}>
                          {PLATFORM_LABELS[code as ClientPlatformFilter] ??
                            code}
                        </Tag>
                      ))}
                      <IssueTriageLabels labels={row.triage_labels} hideEmpty />
                      {row.incident ? (
                        <Tag color="blue">有 incident</Tag>
                      ) : null}
                      {isReviewOverdue(row) ? (
                        <Tag color="error">复查超期</Tag>
                      ) : null}
                    </div>
                  </div>
                </div>
              </article>
            )
          }}
        />
      </Space>

      {/* 放在 Space 外：sticky 需要以整张列表为包含块，否则吸不住底 */}
      {isMobile && selectedKeys.length > 0 ? (
        <div
          className="admin-mobile-bulkbar"
          role="region"
          aria-label="批量操作"
        >
          <span className="admin-mobile-bulkbar__count">
            本页已选 {selectedKeys.length} 个
          </span>
          <Button onClick={() => setSelectedKeys([])}>取消</Button>
          <Button
            type="primary"
            disabled={selectedKeys.length < 2}
            onClick={() => setMergeOpen(true)}
          >
            合并所选
          </Button>
        </div>
      ) : null}

      <MergeIssuesModal
        open={mergeOpen}
        rows={selectedRows}
        onClose={() => setMergeOpen(false)}
        onMerged={() => {
          setMergeOpen(false)
          setSelectedKeys([])
          invalidateIssueQueries(qc)
        }}
        message={message}
      />

      <IssueDrawer
        issueId={detailId}
        onClose={() => setDetailId(null)}
        onChanged={(id) => invalidateIssueQueries(qc, id)}
        message={message}
      />
    </>
  )
}

function MergeIssuesModal({
  open,
  rows,
  onClose,
  onMerged,
  message,
}: {
  open: boolean
  rows: Array<IssueRow>
  onClose: () => void
  onMerged: () => void
  message: ReturnType<typeof AntdApp.useApp>['message']
}) {
  const [targetId, setTargetId] = useState<string>()
  const [rationale, setRationale] = useState('')
  const [idempotencyKey, setIdempotencyKey] = useState('')

  useEffect(() => {
    if (!open) return
    setTargetId(rows[0]?.id)
    setRationale('')
    setIdempotencyKey(newIdempotencyKey())
  }, [open, rows])

  const sources = rows.filter((row) => row.id !== targetId)
  const impact = sumIssueImpact(rows)

  const mergeMutation = useMutation({
    mutationFn: async () => {
      if (!targetId || sources.length === 0 || !rationale.trim()) {
        throw new Error('invalid_request')
      }
      const data = await adminRpc('merge_episode_feedback_issues', {
        p_source_ids: sources.map((row) => row.id),
        p_target_id: targetId,
        p_rationale: rationale.trim(),
        p_idempotency_key: idempotencyKey,
        p_expected_versions: expectedVersionsMap(rows),
      })
      if (!data?.ok)
        throw new Error(rpcErrorMessage(data ?? { error: 'unknown' }))
      return data
    },
    onSuccess: () => {
      message.success('已合并到目标问题。合并不发回复。')
      onMerged()
    },
    onError: (error: Error) => {
      message.error(error.message || '合并失败，请刷新后重试')
    },
  })

  return (
    <Modal
      open={open}
      title="合并问题事件"
      onCancel={onClose}
      onOk={() => mergeMutation.mutate()}
      okButtonProps={{
        disabled: !targetId || sources.length === 0 || !rationale.trim(),
        loading: mergeMutation.isPending,
      }}
      destroyOnHidden
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Alert
          type="info"
          showIcon
          message={currentPageSelectionLabel(rows.length)}
          description={`当前页所选合计 ${impact.occurrences} 条报告 / ${impact.users} 人次 / ${impact.episodes} 集次。跨问题用户未去重。合并不发送恢复通知。`}
        />
        <Select
          style={{ width: '100%' }}
          value={targetId}
          onChange={setTargetId}
          options={rows.map((row) => ({
            value: row.id,
            label: `${ISSUE_STATUS_LABELS[row.status]} · ${reasonLabel(row.category)} · ${row.occurrence_count} 报 · ${row.id.slice(0, 8)}`,
          }))}
        />
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          来源 {sources.length} 个问题将离开开放列表并并入目标。
        </Typography.Text>
        <Input.TextArea
          value={rationale}
          onChange={(e) => setRationale(e.target.value)}
          placeholder="合并理由（必填，写入审计）"
          autoSize={{ minRows: 2, maxRows: 4 }}
          maxLength={300}
          showCount
          // 计数在输入框下方绝对定位，留出空间避免压到弹窗底部按钮
          style={{ marginBottom: 16 }}
        />
      </Space>
    </Modal>
  )
}

function IssueDrawer({
  issueId,
  onClose,
  onChanged,
  message,
}: {
  issueId: string | null
  onClose: () => void
  onChanged: (issueId: string) => void
  message: ReturnType<typeof AntdApp.useApp>['message']
}) {
  const isMobile = useIsMobile()
  const [occurrencePage, setOccurrencePage] = useState(1)
  const [selectedOccurrenceIds, setSelectedOccurrenceIds] = useState<string[]>(
    [],
  )
  const [replyBody, setReplyBody] = useState('')
  const [nextStatus, setNextStatus] = useState<IssueStatus>('observing')
  const [internalNote, setInternalNote] = useState('')
  const [replyKey, setReplyKey] = useState('')
  const [splitTargetId, setSplitTargetId] = useState('')
  const [splitRationale, setSplitRationale] = useState('')
  const [splitKey, setSplitKey] = useState('')
  const [incidentId, setIncidentId] = useState('')
  const [incidentRationale, setIncidentRationale] = useState('')
  const [registerSource, setRegisterSource] = useState<'manual' | 'xf-status'>(
    'manual',
  )
  const [registerExternalId, setRegisterExternalId] = useState('')
  const [registerTitle, setRegisterTitle] = useState('')
  const [registerReasons, setRegisterReasons] = useState<FeedbackReason[]>([])
  const [registerPublicUrl, setRegisterPublicUrl] = useState('')
  const [registerSourceState, setRegisterSourceState] = useState<
    'ok' | 'stale' | 'unknown'
  >('ok')
  const [transferRow, setTransferRow] = useState<OccurrenceRow | null>(null)

  const detailQuery = useQuery({
    queryKey: ['episode_feedback_issue', issueId],
    enabled: !!issueId,
    queryFn: async () =>
      parseIssueDetail(
        await adminRpc('get_episode_feedback_issue', { p_issue_id: issueId }),
      ),
  })

  const occurrenceQuery = useQuery({
    queryKey: ['episode_feedback_issue_occurrences', issueId, occurrencePage],
    enabled: !!issueId,
    placeholderData: keepPreviousData,
    queryFn: async () =>
      parseOccurrenceList(
        await adminRpc('list_episode_feedback_issue_occurrences', {
          p_issue_id: issueId,
          p_limit: PAGE_SIZE,
          p_offset: (occurrencePage - 1) * PAGE_SIZE,
        }),
      ),
  })

  const issue = detailQuery.data?.issue
  const replies = detailQuery.data?.replies ?? []
  const audit = detailQuery.data?.audit ?? []
  const { publicReplies, internalReplies } = splitIssueReplies(replies)
  const occurrences = occurrenceQuery.data?.rows ?? []
  const occurrenceTotal = occurrenceQuery.data?.total ?? 0
  const scope = issue ? formatIssueScope(issue) : null
  const replyBodyTrimmed = replyBody.trim()
  const internalNoteTrimmed = internalNote.trim()
  const canReply =
    replyBodyTrimmed.length >= 1 &&
    replyBodyTrimmed.length <= 500 &&
    internalNoteTrimmed.length <= 500

  useEffect(() => {
    setOccurrencePage(1)
    setSelectedOccurrenceIds([])
    setReplyBody('')
    setInternalNote('')
    setReplyKey(newIdempotencyKey())
    setSplitTargetId('')
    setSplitRationale('')
    setSplitKey(newIdempotencyKey())
    setIncidentId('')
    setIncidentRationale('')
    setRegisterExternalId('')
    setRegisterTitle('')
    setRegisterReasons([])
    setRegisterPublicUrl('')
    setRegisterSourceState('ok')
  }, [issueId])

  useEffect(() => {
    if (!issue) return
    setNextStatus(issue.status)
  }, [issue])

  const writeError = (error: Error) => {
    message.error(error.message || '操作失败，请刷新后重试')
    if (issueId) onChanged(issueId)
  }

  const replyMutation = useMutation({
    mutationFn: async () => {
      if (!issue || !canReply) {
        throw new Error(rpcErrorMessage({ error: 'invalid_request' }))
      }
      const data = await adminRpc('reply_episode_feedback_issue', {
        p_issue_id: issue.id,
        p_body: replyBodyTrimmed,
        p_status: nextStatus,
        p_internal_note: internalNoteTrimmed || null,
        p_expected_version: issue.version,
        p_idempotency_key: replyKey,
      })
      if (!data?.ok)
        throw new Error(rpcErrorMessage(data ?? { error: 'unknown' }))
      return data
    },
    onSuccess: () => {
      message.success('已回复并更新。涉及用户将收到通知。')
      setReplyBody('')
      setInternalNote('')
      setReplyKey(newIdempotencyKey())
      if (issueId) onChanged(issueId)
    },
    onError: writeError,
  })

  const splitMutation = useMutation({
    mutationFn: async () => {
      if (
        !issue ||
        selectedOccurrenceIds.length === 0 ||
        !splitRationale.trim()
      ) {
        throw new Error(rpcErrorMessage({ error: 'invalid_request' }))
      }
      if (splitTargetId && !isUuid(splitTargetId)) {
        throw new Error('目标问题需要完整 UUID')
      }
      const data = await adminRpc('split_episode_feedback_issue', {
        p_issue_id: issue.id,
        p_occurrence_ids: selectedOccurrenceIds,
        p_target_id: splitTargetId.trim() || null,
        p_rationale: splitRationale.trim(),
        p_idempotency_key: splitKey,
        p_expected_version: issue.version,
      })
      if (!data?.ok)
        throw new Error(rpcErrorMessage(data ?? { error: 'unknown' }))
      return data
    },
    onSuccess: () => {
      message.success('已拆出选中报告。合并拆分不发回复。')
      setSelectedOccurrenceIds([])
      setSplitKey(newIdempotencyKey())
      if (issueId) onChanged(issueId)
    },
    onError: writeError,
  })

  const linkMutation = useMutation({
    mutationFn: async () => {
      if (!issue || !isUuid(incidentId) || !incidentRationale.trim()) {
        throw new Error(rpcErrorMessage({ error: 'invalid_request' }))
      }
      const data = await adminRpc('link_episode_feedback_issue_incident', {
        p_issue_id: issue.id,
        p_incident_id: incidentId.trim(),
        p_rationale: incidentRationale.trim(),
        p_expected_version: issue.version,
      })
      if (!data?.ok)
        throw new Error(rpcErrorMessage(data ?? { error: 'unknown' }))
      return data
    },
    onSuccess: () => {
      message.success('已关联 incident。关联不是已解决。')
      if (issueId) onChanged(issueId)
    },
    onError: writeError,
  })

  const unlinkMutation = useMutation({
    mutationFn: async () => {
      if (!issue) throw new Error('not_found')
      const data = await adminRpc('unlink_episode_feedback_issue_incident', {
        p_issue_id: issue.id,
        p_expected_version: issue.version,
      })
      if (!data?.ok)
        throw new Error(rpcErrorMessage(data ?? { error: 'unknown' }))
      return data
    },
    onSuccess: () => {
      message.success('已解除 incident 关联')
      if (issueId) onChanged(issueId)
    },
    onError: writeError,
  })

  const registerMutation = useMutation({
    mutationFn: async () => {
      if (!registerExternalId.trim() || !registerTitle.trim()) {
        throw new Error(rpcErrorMessage({ error: 'invalid_request' }))
      }
      const data = await adminRpc('upsert_episode_feedback_incident', {
        p_source: registerSource,
        p_external_id: registerExternalId.trim(),
        p_title: registerTitle.trim(),
        p_reasons: registerReasons,
        p_public_url: registerPublicUrl.trim() || null,
        p_source_state: registerSourceState,
      })
      if (!data?.ok)
        throw new Error(rpcErrorMessage(data ?? { error: 'unknown' }))
      return data
    },
    onSuccess: (data) => {
      const createdId = data?.incident?.id
      if (typeof createdId === 'string') setIncidentId(createdId)
      message.success('已登记本地 incident，确认范围后再关联。')
    },
    onError: writeError,
  })

  const occurrenceColumns: ColumnsType<OccurrenceRow> = [
    {
      title: '剧集',
      render: (_: unknown, row) => (
        <Space direction="vertical" size={0}>
          <Typography.Text style={{ fontSize: 13 }}>
            {row.anime_title || `番剧 #${row.anime_id}`}
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            {formatEpisodeLabel(row.episode_kind, row.episode_number)}
            {row.episode_title ? ` · ${row.episode_title}` : ''}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: '报告',
      width: 170,
      render: (_: unknown, row) => (
        <Space direction="vertical" size={0}>
          <Tag color={reasonColor(row.reason)}>{reasonLabel(row.reason)}</Tag>
          <Tag color={occurrenceStatusColor(row.status)}>
            {OCCURRENCE_STATUS_LABELS[row.status]}
          </Tag>
          {row.follow_up_of ? <Tag color="orange">恢复后再次反馈</Tag> : null}
        </Space>
      ),
    },
    {
      title: '标签',
      width: 170,
      render: (_: unknown, row) => (
        <OccurrenceTriage
          row={row}
          onChanged={() => issueId && onChanged(issueId)}
        />
      ),
    },
    {
      title: '播放',
      width: 150,
      render: (_: unknown, row) => (
        <PlaybackContextTags playerCode={row.player_code} isHls={row.is_hls} />
      ),
    },
    {
      title: '时间',
      width: 150,
      render: (_: unknown, row) => <OccurrenceTime row={row} />,
    },
  ]

  return (
    <Drawer
      open={!!issueId}
      onClose={onClose}
      width={isMobile ? '100%' : 760}
      title={
        issue ? `问题事件 · ${ISSUE_STATUS_LABELS[issue.status]}` : '问题事件'
      }
      destroyOnHidden
    >
      {detailQuery.isLoading ? (
        <Typography.Text type="secondary">加载中…</Typography.Text>
      ) : detailQuery.isError ? (
        <Alert
          type="error"
          showIcon
          message={
            detailQuery.error instanceof Error
              ? detailQuery.error.message
              : '问题详情加载失败'
          }
        />
      ) : !issue ? null : (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message="看证据后回复并更新。公开回复会通知该事件涉及的用户。"
            description="打开播放页只是辅助检查。一人的 IP 不能代表整件事。排除结论写核查依据，不要指责用户。"
          />

          <Descriptions
            column={1}
            size="small"
            bordered
            layout={isMobile ? 'vertical' : 'horizontal'}
            items={[
              {
                label: '番 / 集',
                children: (
                  <Space direction="vertical" size={0}>
                    <span>{scope?.title}</span>
                    {scope?.subtitle ? (
                      <Typography.Text type="secondary">
                        {scope.subtitle}
                      </Typography.Text>
                    ) : null}
                  </Space>
                ),
              },
              {
                label: '状态',
                children: (
                  <Space wrap>
                    <Tag color={issueStatusColor(issue.status)}>
                      {ISSUE_STATUS_LABELS[issue.status]}
                    </Tag>
                    <Tag>{reasonLabel(issue.category)}</Tag>
                    {issue.assignment_locked ? <Tag>归属已锁定</Tag> : null}
                  </Space>
                ),
              },
              {
                label: '影响',
                children: `${issue.distinct_user_count} 名独立用户 · ${issue.distinct_episode_count} 集 · ${issue.occurrence_count} 条报告`,
              },
              {
                label: '线路 / 平台',
                children: (
                  <Space wrap>
                    {issue.player_codes.length > 0
                      ? issue.player_codes.map((code) => (
                          <Tag key={code} color="cyan">
                            {code}
                          </Tag>
                        ))
                      : '未知通道'}
                    {issue.platforms.map((code) => (
                      <Tag key={code}>
                        {PLATFORM_LABELS[code as ClientPlatformFilter] ?? code}
                      </Tag>
                    ))}
                  </Space>
                ),
              },
              {
                label: '发生时间',
                children: `${formatCST(issue.first_occurred_at)} → ${formatCST(issue.last_occurred_at)} CST`,
              },
              {
                label: '复查期限',
                children: issue.review_due_at
                  ? `${formatCST(issue.review_due_at)} CST${isReviewOverdue(issue) ? ' · 已超期' : ''}`
                  : '未设置',
              },
              {
                label: 'Incident',
                children: issue.incident ? (
                  <Space direction="vertical" size={0}>
                    <span>{issue.incident.title}</span>
                    <Typography.Text type="secondary">
                      {issue.incident.state === 'resolved'
                        ? '源已恢复 · 不能自动宣称用户侧已修好'
                        : '进行中'}
                    </Typography.Text>
                  </Space>
                ) : (
                  '未关联'
                ),
              },
            ]}
          />

          <Divider titlePlacement="start" plain>
            原始报告
          </Divider>
          {occurrenceQuery.isError ? (
            <Alert type="error" showIcon message="原始报告加载失败" />
          ) : (
            <>
              <div className="admin-table-wrap">
                <Table<OccurrenceRow>
                  rowKey="occurrence_id"
                  size="small"
                  loading={occurrenceQuery.isLoading}
                  columns={occurrenceColumns}
                  dataSource={occurrences}
                  rowSelection={{
                    selectedRowKeys: selectedOccurrenceIds,
                    preserveSelectedRowKeys: false,
                    onChange: (keys) =>
                      setSelectedOccurrenceIds(keys as string[]),
                  }}
                  scroll={{ x: 820 }}
                  expandable={{
                    expandedRowRender: (row) => (
                      <OccurrenceEvidence
                        row={row}
                        onTransfer={() => setTransferRow(row)}
                      />
                    ),
                  }}
                  pagination={{
                    current: occurrencePage,
                    pageSize: PAGE_SIZE,
                    total: occurrenceTotal,
                    showSizeChanger: false,
                    showTotal: (totalRows) => `共 ${totalRows} 条原始报告`,
                    onChange: (next) => {
                      setOccurrencePage(next)
                      setSelectedOccurrenceIds([])
                    },
                  }}
                />
              </div>
              {/* 手机：原始报告改为可勾选的卡片，证据折叠在卡片内 */}
              <AdminMobileList
                rows={occurrences}
                rowKey={(row) => row.occurrence_id}
                loading={occurrenceQuery.isLoading}
                page={occurrencePage}
                pageSize={PAGE_SIZE}
                total={occurrenceTotal}
                onPageChange={(next) => {
                  setOccurrencePage(next)
                  setSelectedOccurrenceIds([])
                }}
                emptyContent={
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      occurrenceQuery.isLoading ? '加载中…' : '暂无原始报告'
                    }
                  />
                }
                renderItem={(row) => (
                  <OccurrenceMobileCard
                    row={row}
                    selected={selectedOccurrenceIds.includes(row.occurrence_id)}
                    onSelect={(checked) =>
                      setSelectedOccurrenceIds((prev) =>
                        checked
                          ? [...prev, row.occurrence_id]
                          : prev.filter((id) => id !== row.occurrence_id),
                      )
                    }
                    onTriageChanged={() => issueId && onChanged(issueId)}
                    onTransfer={() => setTransferRow(row)}
                  />
                )}
              />
            </>
          )}

          <Divider titlePlacement="start" plain>
            公开回复
          </Divider>
          {publicReplies.length === 0 ? (
            <Typography.Text type="secondary">
              还没有面向用户的回复
            </Typography.Text>
          ) : (
            <Timeline
              items={publicReplies.map((reply) => replyTimelineItem(reply))}
            />
          )}

          <Divider titlePlacement="start" plain>
            回复并更新
          </Divider>
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <Select<IssueStatus>
              style={{ width: '100%' }}
              value={nextStatus}
              onChange={setNextStatus}
              options={SETTABLE_ISSUE_STATUSES.map((value) => ({
                value,
                label: ISSUE_STATUS_LABELS[value],
              }))}
            />
            <ReplyTemplatePicker
              context="episode_feedback_issue"
              actions={[nextStatus]}
              facet={issue.category}
              value={replyBody}
              onChange={setReplyBody}
              maxLength={500}
            />
            <Input.TextArea
              value={replyBody}
              onChange={(e) => setReplyBody(e.target.value)}
              placeholder={
                nextStatus === 'no_anomaly'
                  ? '面向用户说明核查依据（必填）。未发现异常不等于没复现或暂不处理。'
                  : '面向用户的回复（必填）。保持或更改状态都会通知涉及用户。'
              }
              autoSize={{ minRows: 3, maxRows: 6 }}
              maxLength={500}
              showCount
            />
            <Collapse
              ghost
              items={[
                {
                  key: 'internal-note',
                  label: '内部备注（不通知用户）',
                  children: (
                    <Input.TextArea
                      value={internalNote}
                      onChange={(e) => setInternalNote(e.target.value)}
                      placeholder="只给同事看，可空"
                      autoSize={{ minRows: 2, maxRows: 4 }}
                      maxLength={500}
                      showCount
                    />
                  ),
                },
              ]}
            />
            <Button
              type="primary"
              loading={replyMutation.isPending}
              disabled={!canReply}
              onClick={() => replyMutation.mutate()}
            >
              回复并更新
            </Button>
          </Space>

          <Collapse
            items={[
              {
                key: 'more',
                label: '更多操作',
                children: (
                  <Space
                    direction="vertical"
                    size={16}
                    style={{ width: '100%' }}
                  >
                    <Typography.Text type="secondary">
                      合并拆分、incident 登记关联和审计不是日常必填。没有服务端
                      incident 名称检索，关联仍用已登记记录。
                    </Typography.Text>

                    <Divider titlePlacement="start" plain>
                      拆分当前页勾选
                    </Divider>
                    <Space
                      direction="vertical"
                      size={8}
                      style={{ width: '100%' }}
                    >
                      <Typography.Text
                        type="secondary"
                        style={{ fontSize: 12 }}
                      >
                        拆分只作用于当前页勾选的 {selectedOccurrenceIds.length}{' '}
                        条报告，不会发回复。
                      </Typography.Text>
                      <Input
                        value={splitTargetId}
                        onChange={(e) => setSplitTargetId(e.target.value)}
                        placeholder="拆入已有问题 UUID；留空则新建问题"
                      />
                      <Input.TextArea
                        value={splitRationale}
                        onChange={(e) => setSplitRationale(e.target.value)}
                        placeholder="拆分理由（必填）"
                        autoSize={{ minRows: 2, maxRows: 3 }}
                      />
                      <Button
                        loading={splitMutation.isPending}
                        disabled={
                          selectedOccurrenceIds.length === 0 ||
                          !splitRationale.trim()
                        }
                        onClick={() => splitMutation.mutate()}
                      >
                        拆出选中报告
                      </Button>
                    </Space>

                    <Divider titlePlacement="start" plain>
                      Incident
                    </Divider>
                    <Space
                      direction="vertical"
                      size={8}
                      style={{ width: '100%' }}
                    >
                      {issue.incident ? (
                        <Typography.Text>
                          当前：{issue.incident.title}（
                          {issue.incident.state === 'resolved'
                            ? '已恢复'
                            : '进行中'}
                          ）
                        </Typography.Text>
                      ) : (
                        <Typography.Text type="secondary">
                          尚未关联。范围不明时不要强行挂上。
                        </Typography.Text>
                      )}
                      <Input
                        value={incidentId}
                        onChange={(e) => setIncidentId(e.target.value)}
                        placeholder="已登记 incident 的 UUID"
                      />
                      <Input.TextArea
                        value={incidentRationale}
                        onChange={(e) => setIncidentRationale(e.target.value)}
                        placeholder="关联依据（必填）"
                        autoSize={{ minRows: 2, maxRows: 3 }}
                      />
                      <Space wrap>
                        <Button
                          loading={linkMutation.isPending}
                          disabled={
                            !isUuid(incidentId) || !incidentRationale.trim()
                          }
                          onClick={() => linkMutation.mutate()}
                        >
                          关联
                        </Button>
                        <Button
                          loading={unlinkMutation.isPending}
                          disabled={!issue.incident}
                          onClick={() => unlinkMutation.mutate()}
                        >
                          解除关联
                        </Button>
                      </Space>
                      <Typography.Text type="secondary">
                        历史源不可用时，先按名称登记：
                      </Typography.Text>
                      <Select
                        value={registerSource}
                        onChange={setRegisterSource}
                        options={[
                          { value: 'manual', label: '手工登记' },
                          { value: 'xf-status', label: 'xf-status' },
                        ]}
                      />
                      <Input
                        value={registerTitle}
                        onChange={(e) => setRegisterTitle(e.target.value)}
                        placeholder="incident 名称"
                      />
                      <Input
                        value={registerExternalId}
                        onChange={(e) => setRegisterExternalId(e.target.value)}
                        placeholder="外部 ID / 手工编号"
                      />
                      <Select
                        mode="multiple"
                        allowClear
                        style={{ width: '100%' }}
                        placeholder="匹配症状（可选）"
                        value={registerReasons}
                        onChange={setRegisterReasons}
                        options={(
                          Object.entries(REASON_LABELS) as Array<
                            [FeedbackReason, string]
                          >
                        ).map(([value, label]) => ({ value, label }))}
                      />
                      <Input
                        value={registerPublicUrl}
                        onChange={(e) => setRegisterPublicUrl(e.target.value)}
                        placeholder="状态页 https 链接（可选）"
                      />
                      <Select
                        value={registerSourceState}
                        onChange={setRegisterSourceState}
                        options={[
                          { value: 'ok', label: '状态源可用' },
                          { value: 'stale', label: '状态源过期' },
                          { value: 'unknown', label: '状态未知' },
                        ]}
                      />
                      <Button
                        loading={registerMutation.isPending}
                        disabled={
                          !registerExternalId.trim() || !registerTitle.trim()
                        }
                        onClick={() => registerMutation.mutate()}
                      >
                        登记本地 incident
                      </Button>
                    </Space>

                    <Divider titlePlacement="start" plain>
                      内部备注与审计
                    </Divider>
                    {internalReplies.length === 0 ? (
                      <Typography.Text type="secondary">
                        暂无内部备注
                      </Typography.Text>
                    ) : (
                      <Timeline
                        items={internalReplies.map((reply) =>
                          replyTimelineItem(reply),
                        )}
                      />
                    )}
                    {audit.length === 0 ? (
                      <Typography.Text type="secondary">
                        暂无归属迁移记录
                      </Typography.Text>
                    ) : (
                      <Timeline
                        items={audit.map((event) => ({
                          children: (
                            <Space direction="vertical" size={0}>
                              <Typography.Text>
                                {event.actor_kind || 'system'} ·{' '}
                                {event.confidence || '—'} ·{' '}
                                {event.rule_version || '—'}
                              </Typography.Text>
                              <Typography.Text
                                type="secondary"
                                style={{ fontSize: 12 }}
                              >
                                {event.rationale || '无理由'} ·{' '}
                                {formatCST(event.created_at)} CST
                              </Typography.Text>
                            </Space>
                          ),
                        }))}
                      />
                    )}
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      规则 {issue.rule_version || '—'}
                      {issue.rationale ? ` · ${issue.rationale}` : ''}
                    </Typography.Text>
                    <Typography.Text
                      type="secondary"
                      copyable={{ text: issue.id }}
                      style={{
                        fontSize: 11,
                        fontFamily: 'var(--ant-font-family-code, monospace)',
                      }}
                    >
                      事件 {issue.id}
                    </Typography.Text>
                    {issue.incident ? (
                      <Typography.Text
                        type="secondary"
                        copyable={{ text: issue.incident.id }}
                        style={{
                          fontSize: 11,
                          fontFamily: 'var(--ant-font-family-code, monospace)',
                        }}
                      >
                        incident {issue.incident.id}
                      </Typography.Text>
                    ) : null}
                  </Space>
                ),
              },
            ]}
          />
        </Space>
      )}
      <TransferToProductModal
        row={transferRow}
        onClose={() => setTransferRow(null)}
        onTransferred={() => issueId && onChanged(issueId)}
      />
    </Drawer>
  )
}

function replyTimelineItem(reply: IssueReply) {
  const before = reply.status_before
    ? ISSUE_STATUS_LABELS[reply.status_before as IssueStatus] ||
      reply.status_before
    : null
  const after = reply.status_after
    ? ISSUE_STATUS_LABELS[reply.status_after as IssueStatus] ||
      reply.status_after
    : null
  const statusLine =
    before && after && before !== after
      ? `${before} → ${after}`
      : after || before || null
  return {
    children: (
      <Space direction="vertical" size={0}>
        <Typography.Paragraph
          style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}
        >
          {reply.body}
        </Typography.Paragraph>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {statusLine ? `${statusLine} · ` : ''}
          {formatCST(reply.created_at)} CST
        </Typography.Text>
      </Space>
    ),
  }
}

/** 详情抽屉里的原始报告卡片（< 992px 替代带展开行的表格）。 */
function OccurrenceMobileCard({
  row,
  selected,
  onSelect,
  onTriageChanged,
  onTransfer,
}: {
  row: OccurrenceRow
  selected: boolean
  onSelect: (checked: boolean) => void
  onTriageChanged: () => void
  onTransfer: () => void
}) {
  return (
    <article className="admin-mobile-record episode-feedback-occ">
      <div className="admin-mobile-record__lead">
        <span className="admin-mobile-record__select">
          <Checkbox
            checked={selected}
            aria-label="选择这条报告（用于拆分）"
            onChange={(e) => onSelect(e.target.checked)}
          />
        </span>
        <div className="episode-feedback-card__main">
          <div className="admin-mobile-record__head">
            <div className="admin-mobile-record__title">
              <Typography.Text>
                {row.user_display_name || row.user_username || '已注销'}
              </Typography.Text>
              <div className="admin-mobile-record__meta">
                <span>
                  {formatEpisodeLabel(row.episode_kind, row.episode_number)}
                  {row.episode_title ? ` · ${row.episode_title}` : ''}
                </span>
              </div>
            </div>
            <Tag color={occurrenceStatusColor(row.status)}>
              {OCCURRENCE_STATUS_LABELS[row.status]}
            </Tag>
          </div>
          <div className="admin-mobile-record__badges">
            <Tag color={reasonColor(row.reason)}>{reasonLabel(row.reason)}</Tag>
            <PlaybackContextTags
              playerCode={row.player_code}
              isHls={row.is_hls}
            />
            {row.follow_up_of ? <Tag color="orange">恢复后再次反馈</Tag> : null}
          </div>
        </div>
      </div>
      <div className="admin-mobile-record__body">
        {row.description ? (
          <Typography.Paragraph
            ellipsis={{ rows: 3, expandable: true, symbol: '展开' }}
            style={{ whiteSpace: 'pre-wrap' }}
          >
            {row.description}
          </Typography.Paragraph>
        ) : (
          <Typography.Text type="secondary">无补充说明</Typography.Text>
        )}
      </div>
      <OccurrenceTriage row={row} onChanged={onTriageChanged} />
      <OccurrenceTime row={row} />
      <Collapse
        ghost
        size="small"
        className="episode-feedback-occ__evidence"
        items={[
          {
            key: 'evidence',
            label: '诊断 / 网络 / 操作',
            children: (
              <OccurrenceEvidence
                row={row}
                onTransfer={onTransfer}
                hideDescription
              />
            ),
          },
        ]}
      />
    </article>
  )
}

function OccurrenceTime({ row }: { row: OccurrenceRow }) {
  const occurred = row.occurred_at
    ? formatCST(row.occurred_at, 'MM-DD HH:mm')
    : null
  return (
    <Space direction="vertical" size={0}>
      <Typography.Text style={{ fontSize: 12 }}>
        {occurred ? `发生 ${occurred}` : '发生时间未知'}
        {row.occurred_at && !row.occurred_at_trusted ? ' · 不可信' : ''}
      </Typography.Text>
      <Typography.Text type="secondary" style={{ fontSize: 11 }}>
        接收 {formatCST(row.received_at, 'MM-DD HH:mm')} CST
      </Typography.Text>
    </Space>
  )
}

function OccurrenceEvidence({
  row,
  onTransfer,
  hideDescription,
}: {
  row: OccurrenceRow
  onTransfer: () => void
  /** 卡片里正文已在外层展示，避免重复 */
  hideDescription?: boolean
}) {
  const diagnostics = sanitizeDiagnostics(row.diagnostics)
  const suggested = suggestedReasonText(
    row.triage?.suggested_reason,
    row.reason,
  )
  return (
    <Space direction="vertical" size={8} style={{ width: '100%' }}>
      {hideDescription ? null : (
        <Typography.Paragraph
          style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}
        >
          {row.description || '无补充说明'}
        </Typography.Paragraph>
      )}
      {row.triage?.reason || suggested ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {row.triage?.reason ? `AI 理由：${row.triage.reason}` : ''}
          {row.triage?.reason && suggested ? ' · ' : ''}
          {suggested ?? ''}
        </Typography.Text>
      ) : null}
      <Space wrap>
        <span>{row.user_display_name || row.user_username || '已注销'}</span>
        {row.user_username ? (
          <Typography.Text type="secondary">
            @{row.user_username}
          </Typography.Text>
        ) : null}
        {row.client_platform ? (
          <Tag>{PLATFORM_LABELS[row.client_platform]}</Tag>
        ) : null}
        {row.client_version ? <Tag>{row.client_version}</Tag> : null}
        <Tag>诊断 {row.diagnostics_accepted}</Tag>
      </Space>
      <DiagnosticsSummary
        diagnostics={diagnostics}
        accepted={row.diagnostics_accepted}
      />
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          本条报告的网络信息，不代表整个事件。
        </Typography.Text>
        <IpAddressLookup ip={row.ip_address} />
        {row.user_agent ? (
          <Typography.Text
            type="secondary"
            copyable={{ text: row.user_agent }}
            style={{
              fontSize: 11,
              fontFamily: 'var(--ant-font-family-code, monospace)',
              wordBreak: 'break-all',
            }}
          >
            {row.user_agent}
          </Typography.Text>
        ) : (
          <Typography.Text type="secondary">User-Agent 未知</Typography.Text>
        )}
      </Space>
      <Space wrap>
        <Button
          size="small"
          icon={<ExportOutlined />}
          href={buildPlaybackInspectUrl(row)}
          target="_blank"
          rel="noreferrer"
        >
          打开播放页
        </Button>
        {row.status === 'pending' ? (
          <Tooltip title="这条其实是 App 功能问题时使用：以原报告人身份建一条功能反馈">
            <Button size="small" type="link" onClick={onTransfer}>
              转为功能反馈…
            </Button>
          </Tooltip>
        ) : null}
      </Space>
    </Space>
  )
}

/** 原始报告的 AI 标签 + 建议原因（只读展示）+ 覆盖入口。无描述的报告不会打标。 */
function OccurrenceTriage({
  row,
  onChanged,
}: {
  row: OccurrenceRow
  onChanged: () => void
}) {
  const suggested = suggestedReasonText(
    row.triage?.suggested_reason,
    row.reason,
  )
  return (
    <Space direction="vertical" size={2}>
      <FeedbackTriageTag
        triage={row.triage}
        sourceKind="episode_feedback"
        publicId={row.occurrence_id}
        emptyText={row.description?.trim() ? '未打标' : '无描述，不打标'}
        onChanged={onChanged}
      />
      {suggested ? (
        <Typography.Text type="secondary" style={{ fontSize: 11 }}>
          {suggested}
        </Typography.Text>
      ) : null}
    </Space>
  )
}

/** 事件行的标签汇总（去重后的有效标签），只读。 */
function IssueTriageLabels({
  labels,
  hideEmpty,
}: {
  labels: Array<TriageLabel>
  hideEmpty?: boolean
}) {
  if (labels.length === 0) {
    return hideEmpty ? null : (
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        —
      </Typography.Text>
    )
  }
  return (
    <Space size={[4, 4]} wrap>
      {labels.map((label) => (
        <Tag
          key={label}
          color={TRIAGE_LABEL_COLORS[label]}
          style={{ marginInlineEnd: 0 }}
        >
          {TRIAGE_LABELS[label]}
        </Tag>
      ))}
    </Space>
  )
}

interface TransferFormValues {
  title: string
  issueType?: TransferIssueType
  area?: TransferArea | null
  publicReply: string
}

/**
 * 剧集反馈转功能反馈（低频）。只对 pending 报告开放；幂等键每次打开弹窗生成一次，
 * 重试沿用同一个，所以网络失败后再点不会重复建单或重复通知。
 */
function TransferToProductModal({
  row,
  onClose,
  onTransferred,
}: {
  row: OccurrenceRow | null
  onClose: () => void
  onTransferred: () => void
}) {
  const [form] = Form.useForm<TransferFormValues>()
  const [idempotencyKey, setIdempotencyKey] = useState('')
  const [result, setResult] = useState<TransferResult | null>(null)
  const values = Form.useWatch([], form) as TransferFormValues | undefined

  // 弹窗 destroyOnHidden，表单每次重新挂载回到初始值；这里只换幂等键、清上次结果
  useEffect(() => {
    if (!row) return
    setIdempotencyKey(newIdempotencyKey())
    setResult(null)
  }, [row])

  const errors = validateTransferForm({
    title: values?.title ?? '',
    issueType: values?.issueType,
    area: values?.area ?? null,
    publicReply: values?.publicReply ?? '',
  })
  const valid = Object.keys(errors).length === 0

  const mutation = useMutation({
    mutationFn: async (v: TransferFormValues) => {
      if (!row || !v.issueType)
        throw new Error(rpcErrorMessage({ error: 'invalid_request' }))
      return transferEpisodeFeedbackToProductIssue({
        occurrenceId: row.occurrence_id,
        title: v.title,
        issueType: v.issueType,
        area: v.area ?? null,
        publicReply: v.publicReply,
        idempotencyKey,
      })
    },
    onSuccess: (next) => {
      setResult(next)
      onTransferred()
    },
  })
  const resetMutation = mutation.reset
  useEffect(() => {
    if (row) resetMutation()
  }, [row, resetMutation])

  const submit = () => {
    if (!valid || !values) return
    mutation.mutate(values)
  }

  return (
    <Modal
      open={!!row}
      title="转为功能反馈"
      onCancel={onClose}
      destroyOnHidden
      footer={
        result
          ? [
              <Button key="close" type="primary" onClick={onClose}>
                关闭
              </Button>,
            ]
          : [
              <Button
                key="cancel"
                onClick={onClose}
                disabled={mutation.isPending}
              >
                取消
              </Button>,
              <Button
                key="ok"
                type="primary"
                danger
                disabled={!valid}
                loading={mutation.isPending}
                onClick={submit}
              >
                确认转单
              </Button>,
            ]
      }
    >
      {!row ? null : result ? (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Alert
            type="success"
            showIcon
            message="已转为功能反馈，并已通知用户"
            description={TRANSFER_OUTCOME_MESSAGES[result.outcome]}
          />
          {result.product_issue_id ? (
            <Space wrap>
              <Link
                to="/product-feedback"
                search={{ issue: result.product_issue_id }}
                target="_blank"
              >
                <ExportOutlined /> 打开这条功能反馈
              </Link>
              <Typography.Text
                type="secondary"
                copyable={{ text: result.product_issue_id }}
                style={{
                  fontSize: 11,
                  fontFamily: 'var(--ant-font-family-code, monospace)',
                }}
              >
                {result.product_issue_id}
              </Typography.Text>
            </Space>
          ) : null}
        </Space>
      ) : (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Alert
            type="warning"
            showIcon
            message="确认后不可撤销"
            description={
              <ul style={{ margin: 0, paddingInlineStart: 18 }}>
                <li>
                  以原报告人身份建一条功能反馈；标题与已有问题完全相同时并入那条。
                </li>
                <li>原报告会被排除，并从当前事件里摘除。</li>
                <li>用户会收到一条站内通知，正文是下面「给用户的回复」。</li>
                <li>用户自己的剧集反馈列表里不再显示这条报告。</li>
              </ul>
            }
          />
          <Typography.Paragraph
            type="secondary"
            style={{ marginBottom: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}
            ellipsis={{ rows: 3, expandable: true }}
          >
            原描述：{row.description || '无补充说明'}
          </Typography.Paragraph>
          {mutation.isError ? (
            <Alert
              type="error"
              showIcon
              message={(mutation.error as Error).message || '转单失败'}
              description="可以直接重试，同一次弹窗内重试不会重复建单或重复通知。"
            />
          ) : null}
          <Form<TransferFormValues>
            form={form}
            layout="vertical"
            initialValues={{ title: '', area: 'playback', publicReply: '' }}
            disabled={mutation.isPending}
            onFinish={submit}
          >
            <Form.Item
              name="title"
              label="标题"
              required
              validateStatus={
                values?.title && errors.title ? 'error' : undefined
              }
              help={values?.title && errors.title ? errors.title : undefined}
            >
              <Input
                maxLength={TRANSFER_TITLE_MAX}
                showCount
                placeholder="用一句话概括功能问题，4–120 字"
              />
            </Form.Item>
            <Space size={12} wrap style={{ width: '100%' }}>
              <Form.Item
                name="issueType"
                label="类型"
                required
                style={{ minWidth: 160 }}
              >
                <Select
                  placeholder="选择类型"
                  options={(
                    Object.entries(TRANSFER_ISSUE_TYPE_LABELS) as Array<
                      [TransferIssueType, string]
                    >
                  ).map(([value, label]) => ({ value, label }))}
                />
              </Form.Item>
              <Form.Item name="area" label="区域" style={{ minWidth: 160 }}>
                <Select
                  allowClear
                  placeholder="可空"
                  options={(
                    Object.entries(TRANSFER_AREA_LABELS) as Array<
                      [TransferArea, string]
                    >
                  ).map(([value, label]) => ({ value, label }))}
                />
              </Form.Item>
            </Space>
            <Form.Item
              name="publicReply"
              label="给用户的回复"
              required
              extra="会作为站内通知正文发给用户"
            >
              <Input.TextArea
                autoSize={{ minRows: 3, maxRows: 6 }}
                maxLength={TRANSFER_REPLY_MAX}
                showCount
                placeholder="例如：这个问题属于 App 功能，我们已转到功能反馈跟进，后续进展会在那里通知你。"
              />
            </Form.Item>
          </Form>
        </Space>
      )}
    </Modal>
  )
}

function DiagnosticsSummary({
  diagnostics,
  accepted,
}: {
  diagnostics: ReturnType<typeof sanitizeDiagnostics>
  accepted: OccurrenceRow['diagnostics_accepted']
}) {
  if (accepted === 'absent' || !diagnostics) {
    return (
      <Typography.Text type="secondary">
        没有结构化诊断。没有诊断不等于无效反馈。
      </Typography.Text>
    )
  }

  const items = [
    diagnostics.stage ? `阶段 ${diagnostics.stage}` : null,
    diagnostics.error_code ? `错误码 ${diagnostics.error_code}` : null,
    diagnostics.http_status != null ? `HTTP ${diagnostics.http_status}` : null,
    diagnostics.has_first_frame === true
      ? '已出首帧'
      : diagnostics.has_first_frame === false
        ? '未出首帧'
        : null,
    diagnostics.stall_count != null
      ? `卡顿 ${diagnostics.stall_count} 次`
      : null,
    diagnostics.stall_total_ms != null
      ? `卡顿 ${diagnostics.stall_total_ms}ms`
      : null,
    diagnostics.retry_count != null ? `重试 ${diagnostics.retry_count}` : null,
    diagnostics.route_switch_result
      ? `切线 ${diagnostics.route_switch_result}`
      : null,
    diagnostics.danmaku_symptom ? `弹幕 ${diagnostics.danmaku_symptom}` : null,
    diagnostics.playback_mode ? `模式 ${diagnostics.playback_mode}` : null,
    diagnostics.preflight
      ? `预检 ${diagnostics.preflight.verdict || 'unknown'}${
          diagnostics.preflight.status != null
            ? `/${diagnostics.preflight.status}`
            : ''
        }`
      : null,
  ].filter(Boolean)

  return (
    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
      {items.join(' · ') || '诊断字段均未知'}
      {accepted === 'ignored' ? ' · 诊断版本无法识别，已忽略' : ''}
      {accepted === 'partial' ? ' · 部分诊断字段已丢弃' : ''}
    </Typography.Text>
  )
}

function RawFeedbackView() {
  const qc = useQueryClient()
  const { message } = AntdApp.useApp()
  const isMobile = useIsMobile()
  const [tab, setTab] = useState<FeedbackOccurrenceStatus | 'all'>('pending')
  const [page, setPage] = useState(1)
  const [reason, setReason] = useState<FeedbackReason | undefined>()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounced(search.trim(), 300)
  const [detail, setDetail] = useState<RawFeedbackRow | null>(null)

  useEffect(() => {
    setPage(1)
  }, [tab, reason, debouncedSearch])

  const listQuery = useQuery({
    queryKey: [
      'episode_feedback',
      { tab, page, reason, search: debouncedSearch },
    ],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const data = (await adminRpc('list_episode_feedback', {
        p_status: tab,
        p_reason: reason ?? null,
        p_anime_id: null,
        p_search: debouncedSearch || null,
        p_from: null,
        p_to: null,
        p_limit: PAGE_SIZE,
        p_offset: (page - 1) * PAGE_SIZE,
      })) as RawListResult
      if (!data.ok) throw new Error(data.error ?? 'unknown')
      return { rows: data.rows ?? [], total: data.total ?? 0 }
    },
  })

  const columns: ColumnsType<RawFeedbackRow> = useMemo(
    () => [
      {
        title: '番剧 / 剧集',
        render: (_: unknown, row) => (
          <Space direction="vertical" size={0}>
            <Typography.Text style={{ fontSize: 13 }}>
              {row.anime_title || `番剧 #${row.anime_id}`}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              {formatEpisodeLabel(row.episode_kind, row.episode_number)}
              {row.episode_title ? ` · ${row.episode_title}` : ''}
            </Typography.Text>
          </Space>
        ),
      },
      {
        title: '原因',
        width: 140,
        render: (_: unknown, row) => (
          <Tag color={reasonColor(row.reason)}>{reasonLabel(row.reason)}</Tag>
        ),
      },
      {
        title: '播放',
        width: 140,
        render: (_: unknown, row) => (
          <PlaybackContextTags
            playerCode={row.player_code}
            isHls={row.is_hls}
          />
        ),
      },
      {
        title: '补充说明',
        render: (_: unknown, row) =>
          row.description ? (
            <Tooltip title={row.description} mouseEnterDelay={0.3}>
              <Typography.Paragraph
                ellipsis={{ rows: 2 }}
                style={{ marginBottom: 0, maxWidth: 320, fontSize: 12 }}
              >
                {row.description}
              </Typography.Paragraph>
            </Tooltip>
          ) : (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              —
            </Typography.Text>
          ),
      },
      {
        title: '用户',
        width: 140,
        render: (_: unknown, row) =>
          row.user_id ? (
            <Space direction="vertical" size={0}>
              <Typography.Text style={{ fontSize: 13 }}>
                {row.user_display_name || row.user_username || '—'}
              </Typography.Text>
              {row.user_username ? (
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  @{row.user_username}
                </Typography.Text>
              ) : null}
            </Space>
          ) : (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              已注销
            </Typography.Text>
          ),
      },
      {
        title: '状态',
        width: 90,
        render: (_: unknown, row) => (
          <Tag color={occurrenceStatusColor(row.status)}>
            {OCCURRENCE_STATUS_LABELS[row.status]}
          </Tag>
        ),
      },
      {
        title: '时间',
        width: 130,
        render: (_: unknown, row) => (
          <Typography.Text
            style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}
          >
            {formatCST(row.created_at, 'MM-DD HH:mm')}
          </Typography.Text>
        ),
      },
      {
        title: '操作',
        width: 90,
        fixed: 'right',
        render: (_: unknown, row) => (
          <Button type="link" size="small" onClick={() => setDetail(row)}>
            {row.status === 'pending' ? '受理' : '查看'}
          </Button>
        ),
      },
    ],
    [],
  )

  const rows = listQuery.data?.rows ?? []
  const total = listQuery.data?.total ?? 0

  return (
    <>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Alert
          type="warning"
          showIcon
          message="原始反馈是辅助视图"
          description={
            isMobile
              ? '不要与问题事件视图重复处置，以免重复通知。'
              : '这里仍按单条 occurrence 追溯和兼容旧处置。问题事件视图不要同时再点这里的解决/驳回，以免重复通知。'
          }
        />

        <Segmented<FeedbackOccurrenceStatus | 'all'>
          value={tab}
          onChange={setTab}
          options={[
            { label: '待处理', value: 'pending' },
            { label: '已解决', value: 'resolved' },
            { label: '已驳回', value: 'dismissed' },
            { label: '全部', value: 'all' },
          ]}
        />

        <AdminFilterBar
          activeCount={reason ? 1 : 0}
          primary={
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="搜番剧 / 用户名"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 240 }}
            />
          }
          extra={
            <Tooltip title={isMobile ? '刷新' : undefined}>
              <Button
                icon={<ReloadOutlined />}
                aria-label="刷新"
                onClick={() => listQuery.refetch()}
                loading={listQuery.isFetching && !listQuery.isLoading}
              >
                {isMobile ? null : '刷新'}
              </Button>
            </Tooltip>
          }
        >
          <Select
            allowClear
            placeholder="原因"
            style={{ width: 180 }}
            value={reason}
            onClear={() => setReason(undefined)}
            onChange={setReason}
            options={(
              Object.entries(REASON_LABELS) as Array<[FeedbackReason, string]>
            ).map(([value, label]) => ({ value, label }))}
          />
        </AdminFilterBar>

        {listQuery.isError ? (
          <Alert
            type="error"
            showIcon
            message="原始反馈加载失败"
            description={
              listQuery.error instanceof Error
                ? listQuery.error.message
                : undefined
            }
          />
        ) : null}

        <div className="admin-table-wrap">
          <Table<RawFeedbackRow>
            rowKey="id"
            size="middle"
            loading={listQuery.isLoading}
            columns={columns}
            dataSource={listQuery.isError ? [] : rows}
            scroll={{ x: 1240 }}
            locale={{
              emptyText: listQuery.isError ? (
                <Empty description="原始反馈加载失败" />
              ) : (
                <Empty description="暂无原始反馈" />
              ),
            }}
            pagination={{
              current: page,
              pageSize: PAGE_SIZE,
              total: listQuery.isError ? 0 : total,
              showSizeChanger: false,
              showTotal: (totalRows) => `共 ${totalRows} 条`,
              onChange: setPage,
            }}
          />
        </div>

        <AdminMobileList
          rows={listQuery.isError ? [] : rows}
          rowKey={(row) => row.id}
          loading={listQuery.isLoading || listQuery.isFetching}
          page={page}
          pageSize={PAGE_SIZE}
          total={listQuery.isError ? 0 : total}
          onPageChange={setPage}
          renderItem={(row) => (
            <article
              className="admin-mobile-record"
              role="button"
              tabIndex={0}
              aria-label={`${row.status === 'pending' ? '受理' : '查看'}反馈 #${row.id}`}
              onClick={() => setDetail(row)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setDetail(row)
                }
              }}
            >
              <div className="admin-mobile-record__head">
                <div className="admin-mobile-record__title">
                  <Typography.Text>
                    {row.anime_title || `番剧 #${row.anime_id}`}
                  </Typography.Text>
                  <div className="admin-mobile-record__meta">
                    <span>
                      {formatEpisodeLabel(row.episode_kind, row.episode_number)}
                      {row.episode_title ? ` · ${row.episode_title}` : ''}
                    </span>
                  </div>
                </div>
                <Tag color={occurrenceStatusColor(row.status)}>
                  {OCCURRENCE_STATUS_LABELS[row.status]}
                </Tag>
              </div>
              <div className="admin-mobile-record__badges">
                <Tag color={reasonColor(row.reason)}>
                  {reasonLabel(row.reason)}
                </Tag>
                <PlaybackContextTags
                  playerCode={row.player_code}
                  isHls={row.is_hls}
                />
              </div>
              <div className="admin-mobile-record__body">
                {row.description ? (
                  <Typography.Paragraph ellipsis={{ rows: 2 }}>
                    {row.description}
                  </Typography.Paragraph>
                ) : (
                  <Typography.Text type="secondary">无补充说明</Typography.Text>
                )}
              </div>
              <div className="admin-mobile-record__meta">
                <span>
                  用户 {row.user_display_name || row.user_username || '已注销'}
                </span>
                <span>{formatCST(row.created_at, 'MM-DD HH:mm')}</span>
              </div>
            </article>
          )}
        />
      </Space>

      <ResolveDrawer
        row={detail}
        onClose={() => setDetail(null)}
        onResolved={() => {
          qc.invalidateQueries({ queryKey: ['episode_feedback'] })
          qc.invalidateQueries({ queryKey: ['episode_feedback_stats'] })
          qc.invalidateQueries({ queryKey: ['episode_feedback_issues'] })
          qc.invalidateQueries({ queryKey: ['episode_feedback_issue_stats'] })
        }}
        message={message}
      />
    </>
  )
}

function ResolveDrawer({
  row,
  onClose,
  onResolved,
  message,
}: {
  row: RawFeedbackRow | null
  onClose: () => void
  onResolved: () => void
  message: ReturnType<typeof AntdApp.useApp>['message']
}) {
  const isMobile = useIsMobile()
  const [note, setNote] = useState('')
  const [pendingAction, setPendingAction] = useState<ResolveAction | null>(null)

  useEffect(() => {
    setNote('')
    setPendingAction(null)
  }, [row?.id])

  const resolveMutation = useMutation({
    mutationFn: async (params: { action: ResolveAction; note: string }) => {
      if (!row) throw new Error('no_row')
      const data = (await adminRpc('resolve_episode_feedback', {
        p_id: row.id,
        p_action: params.action,
        p_note: params.note || null,
      })) as { ok: boolean; error?: string }
      if (!data.ok) throw new Error(rpcErrorMessage(data))
      return data
    },
    onSuccess: () => {
      message.success('已处理')
      onResolved()
      onClose()
    },
    onError: (e: Error) => {
      if (e.message === 'already_handled' || e.message.includes('已被处理')) {
        message.error('该反馈已被处理过，请刷新')
      } else {
        message.error(e.message || '处理失败')
      }
    },
  })

  const isPending = row?.status === 'pending'

  return (
    <Drawer
      open={!!row}
      onClose={onClose}
      width={isMobile ? '100%' : 640}
      title={row ? `反馈 #${row.id} · ${isPending ? '受理' : '查看'}` : ''}
      destroyOnHidden
    >
      {!row ? null : (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {row.status !== 'pending' ? (
            <Alert
              type="info"
              showIcon
              message={`该反馈已${OCCURRENCE_STATUS_LABELS[row.status]}`}
              description={
                row.resolver_username
                  ? `处理人：@${row.resolver_username}${
                      row.resolved_at ? ` · ${formatCST(row.resolved_at)}` : ''
                    }`
                  : undefined
              }
            />
          ) : null}

          <Descriptions
            column={1}
            size="small"
            bordered
            layout={isMobile ? 'vertical' : 'horizontal'}
            items={[
              {
                label: '番剧',
                children: (
                  <>
                    《{row.anime_title || `#${row.anime_id}`}》 ·{' '}
                    {formatEpisodeLabel(row.episode_kind, row.episode_number)}
                    {row.episode_title ? ` · ${row.episode_title}` : ''}
                  </>
                ),
              },
              {
                label: '原因',
                children: (
                  <Tag color={reasonColor(row.reason)}>
                    {reasonLabel(row.reason)} · {row.reason}
                  </Tag>
                ),
              },
              {
                label: '播放上下文',
                children: (
                  <Space wrap>
                    <PlaybackContextTags
                      playerCode={row.player_code}
                      isHls={row.is_hls}
                    />
                    <Button
                      size="small"
                      icon={<ExportOutlined />}
                      href={buildPlaybackInspectUrl(row)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      打开播放页
                    </Button>
                  </Space>
                ),
              },
              {
                label: '补充说明',
                children: row.description ? (
                  <Typography.Paragraph
                    style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}
                  >
                    {row.description}
                  </Typography.Paragraph>
                ) : (
                  <Typography.Text type="secondary" italic>
                    —
                  </Typography.Text>
                ),
              },
              {
                label: '提交人',
                children: row.user_id ? (
                  <Space direction="vertical" size={0}>
                    <span>
                      {row.user_display_name || row.user_username || '—'}
                    </span>
                    {row.user_username ? (
                      <Typography.Text type="secondary">
                        @{row.user_username}
                      </Typography.Text>
                    ) : null}
                    <Typography.Text
                      type="secondary"
                      copyable
                      style={{
                        fontSize: 11,
                        fontFamily: 'var(--ant-font-family-code, monospace)',
                      }}
                    >
                      {row.user_id}
                    </Typography.Text>
                  </Space>
                ) : (
                  <Typography.Text type="secondary">已注销</Typography.Text>
                ),
              },
              {
                label: '提交时间',
                children: formatCST(row.created_at),
              },
              {
                label: 'User-Agent',
                children: row.user_agent ? (
                  <Typography.Text
                    type="secondary"
                    copyable={{ text: row.user_agent }}
                    style={{
                      fontSize: 11,
                      fontFamily: 'var(--ant-font-family-code, monospace)',
                      wordBreak: 'break-all',
                    }}
                  >
                    {row.user_agent}
                  </Typography.Text>
                ) : (
                  '—'
                ),
              },
              {
                label: 'IP',
                children: <IpAddressLookup ip={row.ip_address} />,
              },
              ...(row.resolver_note
                ? [
                    {
                      label: '处理备注',
                      children: (
                        <Typography.Paragraph
                          style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}
                        >
                          {row.resolver_note}
                        </Typography.Paragraph>
                      ),
                    },
                  ]
                : []),
            ]}
          />

          {isPending ? (
            <div>
              <Typography.Title level={5} style={{ marginBottom: 8 }}>
                处理面板
              </Typography.Title>
              <div style={{ marginBottom: 8 }}>
                <ReplyTemplatePicker
                  context="episode_feedback"
                  actions={['resolve', 'dismiss']}
                  facet={row.reason}
                  value={note}
                  onChange={setNote}
                  maxLength={500}
                  disabled={resolveMutation.isPending}
                />
              </div>
              <Input.TextArea
                placeholder="备注（可选，会写入审计日志，并附在通知里展示给用户）"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                autoSize={{ minRows: 2, maxRows: 4 }}
                maxLength={500}
                showCount
                style={{ marginBottom: 12 }}
              />
              <Space wrap>
                <Button
                  type="primary"
                  loading={
                    resolveMutation.isPending && pendingAction === 'resolve'
                  }
                  disabled={
                    resolveMutation.isPending && pendingAction !== 'resolve'
                  }
                  onClick={() => {
                    setPendingAction('resolve')
                    resolveMutation.mutate({
                      action: 'resolve',
                      note: note.trim(),
                    })
                  }}
                >
                  标记为已解决
                </Button>
                <Button
                  loading={
                    resolveMutation.isPending && pendingAction === 'dismiss'
                  }
                  disabled={
                    resolveMutation.isPending && pendingAction !== 'dismiss'
                  }
                  onClick={() => {
                    setPendingAction('dismiss')
                    resolveMutation.mutate({
                      action: 'dismiss',
                      note: note.trim(),
                    })
                  }}
                >
                  驳回
                </Button>
              </Space>
            </div>
          ) : null}
        </Space>
      )}
    </Drawer>
  )
}
